import { useEffect, useState } from 'react';

import { Typography, Box } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { RevenuesService } from '~features/finances';

import { useStyles } from './style';
import { Table } from '../../../../../components';
import {
	isFullString,
	isObject,
	isNumber,
	decimalAmount,
	isFullArray,
	getFinanceTableHeaders,
} from '../../../../../shared/utility';
import * as actions from '../../../../../store/actions';

interface BookingsFinanceProps {
	businessRevenue?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	id?: string;
	partnerId?: string;
	date?: string;
	onFetchBusinessRevenue?(...args: unknown[]): unknown;
}

const service = new RevenuesService();

const BookingsFinance = (props: BookingsFinanceProps) => {
	const classes = useStyles();
	const { businessRevenue, id, partnerId, date, onFetchBusinessRevenue } = props;
	const { t } = useTranslation();
	const [finance, setFinance] = useState(null);
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const { data: businessRevenueData, isLoading: businessRevenueLoading } = useSWR(
		[
			service.basePath,
			date,
			service.businessSubPath,
			{
				organisationId: partnerId,
				excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
			},
		],
		([_1, dateSubPath, _2, args]) => service.getBusinessRevenuesUnmapped(dateSubPath, args)
	);

	useEffect(() => {
		const parseId = parseInt(id, 10);
		if (isNumber(parseId) && isObject(businessRevenueData)) {
			const financeData = businessRevenueData.billings.filter((billing) =>
				billing.organisationReference.id == parseId ? { amount: billing.amount } : null
			);
			setFinance(financeData);
		}
	}, [id, businessRevenueData]);

	const tableHeader = getFinanceTableHeaders(false, true, true);

	const tableBody =
		isFullArray(finance) ?
			finance.map((item) => [
				{
					content: (
						<Box vw='510px'>
							<Typography variant='body2'>{businessRevenueData.title}</Typography>
						</Box>
					),
				},
				{ content: decimalAmount(item.amount.subtotal, item.amount) },
				{ content: decimalAmount(item.amount.discount, item.amount) },
				{ content: decimalAmount(item.amount.subtotal - item.amount.discount, item.amount) },
				{ content: decimalAmount(item.amount.priceVat, item.amount) },
				{ content: decimalAmount(item.amount.refund, item.amount) },
				{ content: decimalAmount(item.amount.totalPrice, item.amount) },
			])
		:	Array(1)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	const titleWithInformation = {
		header: t('ui.label.organisationalBilling'),
	};

	return (
		<Table
			body={tableBody}
			cellStyle={classes.cellStyle}
			header={tableHeader}
			isNotPaginate={true}
			loading={businessRevenueLoading}
			titleWithInformation={titleWithInformation}
			withoutPages={true}
		/>
	);
};

const mapStateToProps = (state) => {
	return {
		businessRevenue: state.details.businessRevenue,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchBusinessRevenue: (date, filters) =>
			dispatch(actions.fetchBusinessRevenue(date, filters)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingsFinance);
